<template>
  <ion-page>
    <PageHeaderWithMenu title="Разделы приложения" :scrolled="headerScrolled" />
    <ion-content :class="[isMobile ? 'main-background' : 'liga-web-background']" :fullscreen="true" @ionScroll="onContentScroll">
      <ion-card class="admin-card">
        <ion-card-header>
          <ion-card-title>Доступ к разделам приложения</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <p class="admin-card-subtitle">
            Включите «Доступно всем» для публичных разделов или выберите роль, которая открывает закрытый раздел.
          </p>
          <div v-if="sections.length" class="app-sections-list">
            <div
              v-for="section in sections"
              :key="section.sectionKey"
              class="app-section-row">
              <div class="app-section-info">
                <div class="app-section-title-row">
                  <h3>{{ section.name }}</h3>
                  <span :class="['app-section-status', section.availableToAll ? 'app-section-status--public' : 'app-section-status--role']">
                    {{ section.availableToAll ? 'Доступно всем' : 'По роли' }}
                  </span>
                </div>
                <p class="app-section-key">{{ section.sectionKey }}</p>
                <p class="app-section-role">
                  {{ section.availableToAll ? 'Раздел виден всем пользователям' : roleLabel(section.roleName) }}
                </p>
              </div>

              <div class="app-section-controls">
                <label class="app-section-toggle">
                  <span>Доступно всем</span>
                  <ion-toggle v-model="section.availableToAll" mode="ios" @ionChange="saveSection(section)"></ion-toggle>
                </label>

                <ion-item lines="none" :class="['app-section-role-select', { 'app-section-role-select--disabled': section.availableToAll }]">
                  <ion-select
                    label="Роль доступа"
                    label-placement="stacked"
                    v-model="section.roleName"
                    interface="popover"
                    :interface-options="roleSelectPopoverOptions"
                    mode="ios"
                    placeholder="Выберите роль"
                    :disabled="section.availableToAll"
                    @ionChange="saveSection(section)"
                  >
                    <ion-select-option v-for="a in allAuthorities" :key="a.name" :value="a.name">{{ a.description || a.name }}</ion-select-option>
                  </ion-select>
                </ion-item>
              </div>
            </div>
          </div>
          <p v-else class="admin-hint">Разделы приложения не загружены.</p>
        </ion-card-content>
      </ion-card>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { Authority } from '@/model/authority.model';
import { IAppSection } from '@/model/app-section.model';
import { GlobalContext } from '@/model/globalcontext.model';
import { appSectionService } from '@/services/app-section.service';
import AdminService from '@/services/admin.service';
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonItem,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonToggle,
  toastController,
} from '@ionic/vue';
import PageHeaderWithMenu from '@/components/PageHeaderWithMenu.vue';
import { defineComponent, inject } from 'vue';

export default defineComponent({
  name: 'AdminAppSections',
  components: {
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonContent,
    IonItem,
    IonPage,
    IonSelect,
    IonSelectOption,
    IonToggle,
    PageHeaderWithMenu,
  },
  setup() {
    const globalContext = inject('globalContext') as GlobalContext;
    return { isMobile: globalContext.isMobile };
  },
  data() {
    return {
      adminService: new AdminService(),
      allAuthorities: [] as Authority[],
      sections: [] as IAppSection[],
      savingKey: '',
      roleSelectPopoverOptions: {
        cssClass: 'admin-role-select-popover',
        side: 'top',
        alignment: 'center',
      },
      headerScrolled: false,
    };
  },
  async mounted() {
    await Promise.all([this.loadAuthorities(), this.loadSections()]);
  },
  methods: {
    async loadAuthorities() {
      try {
        this.allAuthorities = await this.adminService.getAdminAuthorities();
      } catch (error) {
        console.error('Failed to load authorities', error);
        await this.showToast('Не удалось загрузить роли', 'danger');
      }
    },
    async loadSections() {
      try {
        this.sections = await this.adminService.getAppSections();
      } catch (error) {
        console.error('Failed to load app sections', error);
        await this.showToast('Не удалось загрузить разделы приложения', 'danger');
      }
    },
    async saveSection(section: IAppSection) {
      if (!section.id) {
        await this.showToast('У раздела нет id', 'danger');
        return;
      }
      if (!section.availableToAll && !section.roleName) {
        await this.showToast('Для закрытого раздела нужно выбрать роль', 'warning');
        return;
      }
      this.savingKey = section.sectionKey;
      try {
        const saved = await this.adminService.updateAppSection(section.id, {
          ...section,
          roleName: section.availableToAll ? null : section.roleName,
        });
        const idx = this.sections.findIndex(s => s.id === saved.id);
        if (idx >= 0) {
          this.sections.splice(idx, 1, saved);
        }
        await appSectionService.loadSections(true);
        await this.showToast('Раздел сохранён', 'success');
      } catch (error) {
        console.error('Failed to save app section', error);
        await this.showToast('Не удалось сохранить раздел', 'danger');
      } finally {
        this.savingKey = '';
      }
    },
    roleLabel(roleName?: string | null): string {
      if (!roleName) {
        return 'Роль не выбрана';
      }
      const authority = this.allAuthorities.find(a => a.name === roleName);
      return authority?.description || roleName;
    },
    onContentScroll(event: CustomEvent) {
      const scrollTop = (event.detail as { scrollTop?: number }).scrollTop || 0;
      this.headerScrolled = scrollTop > 4;
    },
    async showToast(message: string, color: string) {
      const toast = await toastController.create({
        message,
        duration: 1800,
        position: 'top',
        color,
      });
      await toast.present();
    },
  },
});
</script>

<style scoped>
.admin-card {
  margin: 16px;
  --background: var(--ion-color-dark);
}

.admin-card ion-card-title {
  color: var(--ion-text-color);
}

.admin-card ion-card-content {
  color: var(--ion-text-color);
}

.admin-card-subtitle,
.admin-hint {
  margin: 0 0 16px;
  color: var(--ion-text-color);
  opacity: 0.78;
  line-height: 1.35;
}

.app-sections-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.app-section-row {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) minmax(520px, 58%);
  gap: 16px;
  align-items: center;
  padding: 14px;
  border: 1px solid var(--ion-border-color);
  border-radius: 14px;
  background: rgba(0, 0, 0, 0.18);
}

.app-section-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.app-section-info h3 {
  margin: 0;
  color: var(--ion-text-color);
  font-size: 1rem;
  font-weight: 700;
}

.app-section-key,
.app-section-role {
  margin: 4px 0 0;
  color: var(--ion-text-color);
  opacity: 0.72;
  font-size: 0.82rem;
  word-break: break-word;
}

.app-section-status {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 3px 9px;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
}

.app-section-status--public {
  background: var(--ion-color-success);
  color: var(--ion-color-success-contrast);
}

.app-section-status--role {
  background: var(--ion-color-warning);
  color: var(--ion-color-warning-contrast);
}

.app-section-controls {
  display: grid;
  grid-template-columns: auto minmax(320px, 1fr);
  gap: 10px;
  align-items: center;
}

.app-section-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  color: var(--ion-text-color);
  font-size: 0.8rem;
  white-space: nowrap;
}

.app-section-role-select {
  --background: rgba(255, 255, 255, 0.06);
  --background-activated: rgba(255, 255, 255, 0.08);
  --background-hover: rgba(255, 255, 255, 0.08);
  --border-radius: 10px;
  --color: var(--ion-text-color);
  --min-height: 52px;
  border-radius: 10px;
  min-width: 260px;
  width: 100%;
}

.app-section-role-select--disabled {
  opacity: 0.5;
}

@media (max-width: 900px) {
  .app-section-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .app-section-controls {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .app-section-toggle {
    justify-content: space-between;
    width: 100%;
    padding: 2px 0;
  }

  .app-section-role-select {
    min-width: 0;
  }

}

@media (max-width: 480px) {
  .admin-card {
    margin: 12px 10px;
  }

  .app-section-row {
    padding: 12px;
    border-radius: 12px;
  }

  .app-section-info h3 {
    font-size: 0.95rem;
  }

  .app-section-status {
    font-size: 0.68rem;
  }
}

:global(.admin-role-select-popover) {
  --width: min(460px, calc(100vw - 32px));
  --max-height: min(520px, calc(100vh - 96px));
}

:global(.admin-role-select-popover::part(content)) {
  width: min(460px, calc(100vw - 32px));
  max-height: min(520px, calc(100vh - 96px));
}

:global(.admin-role-select-popover ion-content) {
  --padding-top: 4px;
  --padding-bottom: 4px;
}

:global(.admin-role-select-popover ion-item) {
  --min-height: 50px;
}

:global(.admin-role-select-popover ion-label),
:global(.admin-role-select-popover ion-radio::part(label)) {
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  line-height: 1.25;
}
</style>
